import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, Send } from "lucide-react";

/**
 * "Message this person" — click any avatar in the People Strip to open a
 * pre-filled email dialog. Uses the /contracts/{id}/message-person endpoint
 * which reuses the app's Resend integration and records an activity entry so
 * everyone in the contract sees the communication happened.
 *
 * Feature 2 — templates now load from /settings/message-templates (Legal
 * Admin/Ops Admin can edit via the Template Editor). We fall back to the
 * built-in three if the API returns nothing.
 */
const FALLBACK_TEMPLATES = [
  { id: "seed-update", label: "Contract update",
    subject: "Contract #{{cn}} — status update",
    body: `Hi {{first}},\n\nQuick update on Contract #{{cn}} — {{vendor}}. It's currently in **{{stage}}**. I'll let you know as soon as it moves to the next step.\n\nAny questions or new info to share, reply to this thread.\n\nThanks,\n{{me}}` },
  { id: "seed-signature", label: "Signature request",
    subject: "Action needed — signature on Contract #{{cn}}",
    body: `Hi {{first}},\n\nContract #{{cn}} — {{vendor}} is ready for your signature. Please review the final terms in HCD Contract Central and sign at your earliest convenience.\n\nHappy to walk through anything before you sign — just reply here.\n\nThanks,\n{{me}}` },
  { id: "seed-renewal", label: "Renewal reminder",
    subject: "Upcoming renewal — Contract #{{cn}}",
    body: `Hi {{first}},\n\nContract #{{cn}} — {{vendor}} is approaching its renewal window. Please confirm whether the Business Unit intends to renew, renegotiate, or let it lapse so Legal can plan the next steps.\n\nReply with your intent or open the contract in HCD Contract Central for the full history.\n\nThanks,\n{{me}}` },
];

function applyTemplate(t, ctx) {
  const fill = (s) => (s || "")
    .replace(/{{cn}}/g,     ctx.cn || "")
    .replace(/{{vendor}}/g, ctx.vendor || "")
    .replace(/{{stage}}/g,  (ctx.stage || "").replace(/_/g, " "))
    .replace(/{{first}}/g,  ctx.first || "")
    .replace(/{{me}}/g,     ctx.me || "");
  return { subject: fill(t.subject), body: fill(t.body) };
}

export default function MessagePersonModal({ open, contract, target, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tpl, setTpl] = useState("custom");
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [busy, setBusy] = useState(false);

  // Load Legal-managed templates when the modal opens; fall back to defaults.
  useEffect(() => {
    if (!open) return;
    api.get("/settings/message-templates")
      .then(r => {
        const items = r.data?.items || [];
        setTemplates(items.length ? items : FALLBACK_TEMPLATES);
      })
      .catch(() => setTemplates(FALLBACK_TEMPLATES));
  }, [open]);

  useEffect(() => {
    if (!open || !contract || !target) return;
    setTpl("custom");
    setSubject(`[Contract #${contract.contract_number}] ${contract.vendor_legal_name}`);
    setBody(
      `Hi ${target.name?.split(" ")[0] || target.name || ""},\n\n` +
      `I'm reaching out about Contract #${contract.contract_number} — ` +
      `${contract.vendor_legal_name} (${(contract.stage || "").replace(/_/g, " ")}).\n\n` +
      `\n\n— sent from HCD Contract Central`
    );
  }, [open, contract?.contract_number, target?.email, target?.name]);

  const pickTemplate = (key) => {
    setTpl(key);
    if (key === "custom") return;
    const t = templates.find(x => x.id === key || x.key === key);
    if (!t) return;
    const applied = applyTemplate(t, {
      cn: contract?.contract_number,
      vendor: contract?.vendor_legal_name,
      stage: contract?.stage,
      first: target?.name?.split(" ")[0] || target?.name || "",
      me: "",
    });
    setSubject(applied.subject); setBody(applied.body);
  };

  if (!open || !contract || !target) return null;
  if (!target.email) {
    return (
      <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose}
           data-testid="message-person-modal">
        <div className="bg-white rounded-md w-[400px] p-5 text-sm text-center" onClick={e => e.stopPropagation()}>
          <Mail className="w-5 h-5 text-slate-400 mx-auto mb-2" />
          No email on file for <b>{target.name || target.label}</b>. Add one to their intake / roster entry to enable direct messaging.
          <div className="mt-4"><button onClick={onClose} className="h-8 px-3 text-xs border border-slate-200 rounded">Close</button></div>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body are required");
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/message-person`, {
        to_email: target.email, to_name: target.name || target.email,
        subject: subject.trim(), body: body.trim(),
        role_label: target.label || "",
      });
      toast.success(`Email sent to ${target.name || target.email}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose}
         data-testid="message-person-modal">
      <div className="bg-white rounded-md w-[560px] max-w-[92vw] shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#005289]" />
          <div>
            <div className="font-bold text-slate-900 text-sm">Message {target.name}</div>
            <div className="text-[11px] text-slate-500">{target.label} · {target.email}</div>
          </div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Template</span>
            <select value={tpl} onChange={e => pickTemplate(e.target.value)}
                    data-testid="msg-person-template"
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md">
              <option value="custom">— Custom message —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Subject</span>
            <input value={subject} onChange={e => setSubject(e.target.value)}
                   data-testid="msg-person-subject"
                   className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" />
          </label>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Message</span>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={8}
                      data-testid="msg-person-body"
                      className="w-full p-3 text-sm border border-slate-200 rounded-md resize-none focus:border-[#005289] focus:outline-none" />
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          <button onClick={onClose} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="msg-person-submit"
                  className="ml-auto h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy ? "Sending…" : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}
